import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { ChevronRight } from 'lucide-react';
import { menuItems } from './navigation';
import logoIcon from '../../assets/images/logoicon.svg';

const Sidebar = () => {
  const { user, hasPermission } = useAuth();
  const { sidebarCollapsed, toggleSidebar } = useTheme();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = React.useState({});

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const hasSubItems = (item) => {
    return item.subItems && item.subItems.length > 0;
  };

  const renderMenuItem = (item, index) => {
    // Check permission
    if (!hasPermission(item.permission)) {
      return null;
    }

    const Icon = item.icon;
    const isActiveItem = isActive(item.path);
    const hasSubs = hasSubItems(item);
    const isExpanded = expandedMenus[item.title] !== undefined
      ? expandedMenus[item.title]
      : isActiveItem;

    return (
      <div key={index} className="mb-1">
        <NavLink
          to={item.path}
          onClick={(e) => {
            if (hasSubs) {
              setExpandedMenus(prev => ({
                ...prev,
                [item.title]: !isExpanded
              }));
            }
          }}
          className={({ isActive }) =>
            `flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'px-3'} py-2 text-sm font-medium rounded-md transition-colors duration-200 ${isActive
              ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`
          }
          title={sidebarCollapsed ? item.title : undefined}
        >
          <Icon className={`${sidebarCollapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0`} />
          {!sidebarCollapsed && (
            <>
              <span className="flex-1">{item.title}</span>
              {hasSubs && (
                <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'transform rotate-90' : ''}`} />
              )}
            </>
          )}
        </NavLink>

        {/* Sub-items */}
        {hasSubs && !sidebarCollapsed && isExpanded && (
          <div className="ml-4 mt-1 space-y-1">
            {item.subItems.map((subItem, subIndex) => {
              if (!hasPermission(subItem.permission)) {
                return null;
              }

              return (
                <NavLink
                  key={subIndex}
                  to={subItem.path}
                  end
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                    }`
                  }
                >
                  <span className="flex-1">{subItem.title}</span>
                </NavLink>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`sidebar ${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white shadow-lg transition-all duration-300 flex-shrink-0`}>
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} px-4 py-6 border-b border-gray-200`}>
          {!sidebarCollapsed ? (
            <div className="flex items-center">
              <img
                src={logoIcon}
                alt="Public Rides Icon"
                className="h-8 w-8"
              />
              <span className="ml-3 text-lg font-semibold text-gray-900">
                Namma Ooru Taxi
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <img
                src={logoIcon}
                alt="Public Rides Icon"
                onClick={toggleSidebar}
                className="h-8 w-8 cursor-pointer"
              />
            </div>
          )}

          {/* Collapse Button */}
          {!sidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Expand button for collapsed sidebar */}
        {/* {sidebarCollapsed && (
          <div className="flex justify-center py-2 border-b border-gray-200">
            <button
              onClick={toggleSidebar}
              className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )} */}

        {/* Navigation */}
        <nav className={`flex-1 ${sidebarCollapsed ? 'px-2' : 'px-3'} py-4 space-y-1 overflow-y-auto`}>
          {menuItems.map(renderMenuItem)}
        </nav>

        {/* User Info */}
        {!sidebarCollapsed && (
          <div className="px-3 py-4 border-t border-gray-200">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.role}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar; 